import { Menu } from "../model/security/menu.model";

/**
 * A dónde lleva una entrada del menú, como arreglo para routerLink.
 *
 * Si la hoja trae `ruta`, manda ella: es la URL completa y se puso justo
 * para poder colgar la hoja de otro grupo sin tocar su `factory`, que es la
 * identidad que comparan los permisos en el servidor. Sin `ruta` se arma
 * como siempre: factory del padre + factory de la hoja, salvo que la hoja ya
 * empiece con el prefijo del padre (asistencia/monitoreo bajo /asistencia).
 */
export function rutaDeMenu(item: Menu, padre?: Menu): string[] {
  if (item?.ruta && item.ruta.trim()) {
    return ["/", ...segmentos(item.ruta)];
  }
  return unirFactories(padre?.factory, item?.factory);
}

/** La regla histórica del sidebar, aislada para que la use también la página de Configuración. */
export function unirFactories(...factories: Array<string | undefined | null>): string[] {
  const rutas = factories
    .filter((factory): factory is string => !!factory)
    // Sin las diagonales de los extremos. La cola sólo arranca tras un carácter
    // que no es diagonal ($1 lo devuelve): así no retrocede de más con muchas seguidas.
    .map((factory) => factory.trim().replace(/^\/+|([^/])\/+$/g, "$1"))
    .filter(Boolean);

  const ruta = rutas.reduce((actual, siguiente) => {
    if (!actual) {
      return siguiente;
    }
    if (siguiente === actual || siguiente.startsWith(actual + "/")) {
      return siguiente;
    }
    return `${actual}/${siguiente}`;
  }, "");

  return ["/", ...segmentos(ruta)];
}

/**
 * Hojas que dan PERMISO pero no se pintan en el panel lateral: su pantalla
 * se abre desde otro módulo. Su renglón de adm_menu y su asignación a
 * perfiles se QUEDAN, porque el servidor mide con ese factory quién puede
 * entrar (MatrizBuss.exigeAcceso, MatrizRiesgoBuss, el tablero de Riesgos).
 *
 * matrizRiesgosIA (29/09/2026): la Matriz de Riesgos IA se abre desde el
 * tablero de Gestión de Riesgos (tarjeta «Matriz base», botón Abrir), igual
 * que las matrices de cada área. En el panel quedan dos módulos: Gestión de
 * Riesgos y Gestión de Hojas de Apoyo.
 *
 * «Solicitar IA» y «Mantenimiento» (12/10/2026, pedido de Norman: un solo
 * módulo para pedir y seguir): se piden y se siguen dentro de «Nueva
 * solicitud». El servidor sigue midiendo el permiso con esas hojas
 * (SolicitudIABuss.MENU_SOLICITUD, MantenimientoBuss.MENU_SOLICITUD) y el
 * concentrador las usa para saber qué tarjetas enseñar.
 */
const FUERA_DEL_PANEL = new Set<string>([
  "matrizriesgosia",
  "admin/solicitudes-ia/solicitud",
  "admin/mantenimiento/solicitud",
]);

export function seVeEnElPanel(item: Menu): boolean {
  // Sin las diagonales de los extremos, igual que en unirFactories.
  const factory = (item?.factory || "").trim().replace(/^\/+|([^/])\/+$/g, "$1").toLowerCase();
  return !FUERA_DEL_PANEL.has(factory);
}

/**
 * Lo que se pinta de una entrada de primer nivel: un grupo sin hojas que
 * pintar no sale (no queda un encabezado vacío), y una hoja suelta de las
 * que no se pintan tampoco.
 */
export function tieneQuePintar(item: Menu): boolean {
  if (item?.tipo === "sub") {
    return (item.hijos || []).some(seVeEnElPanel);
  }
  return seVeEnElPanel(item);
}

function segmentos(ruta: string): string[] {
  return ruta
    .split("/")
    .map((segmento) => segmento.trim())
    .filter(Boolean);
}
